import {
    ContentPath,
    ContentSection,
    createEmptyBlock,
    createSection,
    ToolContentDocument,
    ToolDetailBlock,
    ToolDetailBlockType,
} from '@easytools/tool-content';

/** Cosa è selezionato nell'editor: la pagina, una sezione o un blocco. */
export type Target =
    | { kind: 'page' }
    | { kind: 'section'; section: number }
    | { kind: 'block'; section: number; index: number };

/** Posizione di un blocco nel documento. */
export interface BlockAt { section: number; index: number }

export const PAGE: Target = { kind: 'page' };

// Formato dei dati trascinati: un blocco esistente ("sezione:indice") o un tipo nuovo dalla libreria.
export const DRAG_MOVE = 'application/x-tcs-move';
export const DRAG_NEW = 'application/x-tcs-new';

const withSections = (doc: ToolContentDocument, sections: ContentSection[]): ToolContentDocument => ({ ...doc, sections });

const replaceAt = <T,>(list: T[], index: number, item: T) => list.map((x, i) => (i === index ? item : x));

export function patchSection(doc: ToolContentDocument, section: number, patch: Partial<ContentSection>): ToolContentDocument {
    return withSections(doc, replaceAt(doc.sections, section, { ...doc.sections[section], ...patch }));
}

export function setBlock(doc: ToolContentDocument, at: BlockAt, block: ToolDetailBlock): ToolContentDocument {
    const section = doc.sections[at.section];
    return patchSection(doc, at.section, { blocks: replaceAt(section.blocks, at.index, block) });
}

/** Il blocco con un testo cambiato al percorso indicato (es. ['items', 2, 'title']). */
export function setText(block: ToolDetailBlock, path: ContentPath, value: string): ToolDetailBlock {
    const write = (node: unknown, depth: number): unknown => {
        const key = path[depth];
        if (depth === path.length - 1) {
            if (Array.isArray(node)) return replaceAt(node, key as number, value);
            return { ...(node as object), [key]: value };
        }
        const child = (node as Record<string | number, unknown>)[key];
        const next = write(child, depth + 1);
        if (Array.isArray(node)) return replaceAt(node, key as number, next);
        return { ...(node as object), [key]: next };
    };
    return write(block, 0) as ToolDetailBlock;
}

/** Inserisce un blocco nuovo. Senza sezioni ne crea una. Restituisce il documento e dove è finito. */
export function insertBlock(doc: ToolContentDocument, at: BlockAt, type: ToolDetailBlockType): [ToolContentDocument, BlockAt] {
    const block = createEmptyBlock(type);
    if (!doc.sections.length) return [withSections(doc, [createSection([block])]), { section: 0, index: 0 }];
    const section = doc.sections[at.section];
    const blocks = [...section.blocks.slice(0, at.index), block, ...section.blocks.slice(at.index)];
    return [patchSection(doc, at.section, { blocks }), at];
}

/** Sposta un blocco; `to` indica la posizione prima dello spostamento. */
export function moveBlock(doc: ToolContentDocument, from: BlockAt, to: BlockAt): [ToolContentDocument, BlockAt] {
    const block = doc.sections[from.section].blocks[from.index];
    let target = to;
    if (from.section === to.section && to.index > from.index) target = { ...to, index: to.index - 1 };
    if (from.section === target.section && from.index === target.index) return [doc, from];
    const removed = doc.sections.map((s, i) => (i === from.section ? { ...s, blocks: s.blocks.filter((_, b) => b !== from.index) } : s));
    const sections = removed.map((s, i) => (i === target.section
        ? { ...s, blocks: [...s.blocks.slice(0, target.index), block, ...s.blocks.slice(target.index)] }
        : s));
    return [withSections(doc, sections), target];
}

export function duplicateBlock(doc: ToolContentDocument, at: BlockAt): [ToolContentDocument, BlockAt] {
    const section = doc.sections[at.section];
    const copy = structuredClone(section.blocks[at.index]);
    const blocks = [...section.blocks.slice(0, at.index + 1), copy, ...section.blocks.slice(at.index + 1)];
    return [patchSection(doc, at.section, { blocks }), { section: at.section, index: at.index + 1 }];
}

export function removeBlock(doc: ToolContentDocument, at: BlockAt): ToolContentDocument {
    const section = doc.sections[at.section];
    return patchSection(doc, at.section, { blocks: section.blocks.filter((_, i) => i !== at.index) });
}

export function addSection(doc: ToolContentDocument, at: number, section: ContentSection = createSection()): ToolContentDocument {
    return withSections(doc, [...doc.sections.slice(0, at), section, ...doc.sections.slice(at)]);
}

/** Dove inserire una sezione nuova: dopo quella selezionata (o del blocco selezionato), altrimenti in fondo. */
export const sectionInsertionPoint = (doc: ToolContentDocument, target: Target) =>
    target.kind === 'page' ? doc.sections.length : target.section + 1;

export function moveSection(doc: ToolContentDocument, from: number, to: number): ToolContentDocument {
    if (to < 0 || to >= doc.sections.length || from === to) return doc;
    const sections = [...doc.sections];
    const [item] = sections.splice(from, 1);
    sections.splice(to, 0, item);
    return withSections(doc, sections);
}

export function duplicateSection(doc: ToolContentDocument, at: number): ToolContentDocument {
    return withSections(doc, [...doc.sections.slice(0, at + 1), structuredClone(doc.sections[at]), ...doc.sections.slice(at + 1)]);
}

export const removeSection = (doc: ToolContentDocument, at: number): ToolContentDocument =>
    withSections(doc, doc.sections.filter((_, i) => i !== at));

/** Posizione del blocco contando su tutto il documento (per il Desktop Agent e i segni "Nuovo"). */
export const flatIndex = (doc: ToolContentDocument, at: BlockAt) =>
    doc.sections.slice(0, at.section).reduce((n, s) => n + s.blocks.length, 0) + at.index;

export function fromFlat(doc: ToolContentDocument, flat: number): BlockAt | null {
    let rest = flat;
    for (let section = 0; section < doc.sections.length; section++) {
        const count = doc.sections[section].blocks.length;
        if (rest < count) return { section, index: rest };
        rest -= count;
    }
    return null;
}

/** Dove inserire un blocco scelto dalla libreria: dopo il blocco selezionato, in fondo alla sezione scelta, o in fondo al documento. */
export function insertionPoint(doc: ToolContentDocument, target: Target): BlockAt {
    if (target.kind === 'block') return { section: target.section, index: target.index + 1 };
    if (target.kind === 'section') return { section: target.section, index: doc.sections[target.section]?.blocks.length ?? 0 };
    const last = doc.sections.length - 1;
    return last < 0 ? { section: 0, index: 0 } : { section: last, index: doc.sections[last].blocks.length };
}

export const parseMove = (value: string): BlockAt | null => {
    const [section, index] = value.split(':').map(Number);
    return Number.isInteger(section) && Number.isInteger(index) ? { section, index } : null;
};
